import sidebar_wine from "../../../assets/admin/sidebar_wine.svg"
import mapPoint from "../../../assets/admin/map-point.svg"
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { useParams } from "react-router-dom";
import QRCode from "react-qr-code";
import { GoDownload } from "react-icons/go";
import { IoPrint } from "react-icons/io5";
import jsPDF from "jspdf";
import defaultImage from "../../../utils/DefaultImage";
import PaginationComponent from "../../../utils/PaginationComponent";
import TableLoader from "../../../utils/TableLoader";
import { motion, AnimatePresence } from "framer-motion";
import ChartDashboard from "./ChartDashboard";
import { IoIosCloseCircleOutline } from "react-icons/io";

export default function RestDashboard() {
  const { rest_slug2 } = useParams();
  const frontendApiUrl = import.meta.env.VITE_FRONTEND_APP_URL;
  const apiUrl = import.meta.env.VITE_METERI_API_BASE_URL;
  const token = localStorage.getItem("token");
  const [isLoading, setIsLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [numberData, setNumberData] = useState([]);
  const [productData, setProductData] = useState([]);
  const [restaurantData, setRestaurantData] = useState([]);

  // Animation variants
  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.3 }
    }
  };

  const getRestDashboardData = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = {
        pageNumber: page,
        size: rowsPerPage,
      };

      // Fetch number stock data
      const responseNumberData = await axios.get(
        `${apiUrl}/api/v1/restaurants/${rest_slug2}/dashboard-data`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      // Fetch product (wine stock) data
      const responseProductData = await axios.get(
        `${apiUrl}/api/v1/restaurants/${rest_slug2}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      // Fetch paginated restaurant data
      const responseRestaurantData = await axios.get(
        `${apiUrl}/api/v1/wine-stock/${rest_slug2}`,
        {
          params,
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const numberData = responseNumberData?.data;
      const productData = responseProductData?.data;
      const restaurantData = responseRestaurantData?.data;

      setNumberData(numberData);
      setProductData(productData);
      setRestaurantData(restaurantData?.data);
      setTotalPages(restaurantData?.total);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setIsLoading(false);
    }
  }, [apiUrl, rest_slug2, token, page, rowsPerPage]);

  const downloadQRCode = (slug) => {
    const svg = document.querySelector(`svg[qr-value="${slug}"]`);

    if (svg) {
      const svgData = new XMLSerializer().serializeToString(svg);
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      const img = new Image();

      img.onload = () => {
        const qrSize = 300;
        const borderThickness = 5;
        const totalSize = qrSize + borderThickness * 2;

        canvas.width = totalSize;
        canvas.height = totalSize;

        ctx.fillStyle = "#000000";
        ctx.fillRect(0, 0, totalSize, totalSize);

        ctx.drawImage(img, borderThickness, borderThickness, qrSize, qrSize);


        const pngFile = canvas.toDataURL("image/png");

        // Trigger download
        const link = document.createElement("a");
        link.href = pngFile;
        link.download = `${slug}-qr-code.png`;
        link.click();
      };

      img.src = `data:image/svg+xml;base64,${btoa(svgData)}`;
    } else {
      console.error("SVG QR code not found");
    }
  };

  const printQRCode = (slug) => {
    const svg = document.querySelector(`svg[qr-value="${slug}"]`);

    if (svg) {
      const svgData = new XMLSerializer().serializeToString(svg);
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      const img = new Image();

      img.onload = () => {
        const qrSize = 300;
        const borderThickness = 5;
        const totalSize = qrSize + borderThickness * 2;

        canvas.width = totalSize;
        canvas.height = totalSize;

        ctx.fillStyle = "#000000";
        ctx.fillRect(0, 0, totalSize, totalSize);

        ctx.drawImage(img, borderThickness, borderThickness, qrSize, qrSize);


        const pngFile = canvas.toDataURL("image/png");


        const pdf = new jsPDF();
        pdf.text(slug, 10, 10);
        pdf.addImage(pngFile, "PNG", 10, 20, 60, 60);
        pdf.save(`${slug}.pdf`);
      };

      img.src = `data:image/svg+xml;base64,${btoa(svgData)}`;
    } else {
      console.error("SVG QR code not found");
    }
  };

  const handleQRCodeClick = () => {
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const handleBackgroundClick = (e) => {
    if (e.target.id === "modal-background") {
      closeModal();
    }
  };

  useEffect(() => {
    getRestDashboardData();
  }, [page, rowsPerPage, getRestDashboardData]);

  return (
    <div className="h-full">
      <AnimatePresence mode="wait">
        <motion.div
          key="dashboard"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="h-full"
        >
          <section className="relative grid xl:grid-cols-3 lg:gap-9 gap-4 mb-10">
            {["wine", "orders", "qr"].map((type, index) => (
              <motion.div
                key={type}
                variants={cardVariants}
                initial="hidden"
                animate="visible"
                transition={{ delay: index * 0.1 }}
                className={`flex justify-between items-center bg-[#F8F8F8] border-[1.5px] border-[#8B24332B] text-[#8B2433] lg:px-8 px-5 rounded-2xl shadow py-4 w-full hover:shadow-md transition-shadow ${type === "qr" ? "sm:px-5 px-2" : ""}`}
              >
                {type !== "qr" ? (
                  <>
                    <motion.div
                      className="bg-white rounded-full w-[60px] h-[60px] flex justify-center items-center transition-transform hover:scale-105"
                      whileHover={{ scale: 1.05 }}
                    >
                      <img
                        src={type === "wine" ? sidebar_wine : mapPoint}
                        height={20}
                        width={20}
                        className="w-[25px] h-[25px] object-contain"
                      />
                    </motion.div>
                    <div>
                      <h3 className="text-base font-bold poppins-medium">
                        {type === "wine" ? "N.Vini" : "N.Ordini"}
                      </h3>
                      <p className="text-right font-sans font-extrabold text-4xl poppins-medium">
                        {numberData[0]?.[type === "wine" ? "totalWines" : "totalOrders"]
                          ? `${numberData[0][type === "wine" ? "totalWines" : "totalOrders"]}+`
                          : "-"}
                      </p>
                    </div>
                  </>
                ) : (
                  <>
                    <motion.div
                      className="flex items-center justify-start flex-col gap-2"
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.3 }}
                    >
                      <motion.div
                        className="p-[14px] rounded-full w-[75px] h-[75px] flex justify-center items-center relative bg-[#8B2433] cursor-pointer"
                        onClick={handleQRCodeClick}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        <QRCode
                          value={frontendApiUrl && productData?.slug ? `${frontendApiUrl}restaurant/${productData.slug}` : "No content available"}
                          bgColor="transparent"
                          fgColor="white"
                          size={50}
                          qr-value={productData?.slug}
                        />
                      </motion.div>
                      <motion.div
                        className="flex items-center justify-start gap-2"
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.4 }}
                      >
                        <motion.button
                          className="border py-1 px-2 rounded-xl text-[12px] hover:bg-[#8B2433] hover:text-white transition-colors"
                          onClick={() => printQRCode(productData?.slug)}
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                        >
                          <IoPrint size={"17px"} />
                        </motion.button>
                        <motion.button
                          className="border py-1 px-2 rounded-xl text-[12px] hover:bg-[#8B2433] hover:text-white transition-colors"
                          onClick={() => downloadQRCode(productData?.slug)}
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                        >
                          <GoDownload size={"17px"} />
                        </motion.button>
                      </motion.div>
                    </motion.div>

                    <motion.div
                      className="text-end break-all"
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.3 }}
                    >
                      <h3 className="text-base font-bold poppins-medium">{productData?.company_name || "N/A"}</h3>
                      <h3 className="text-base font-bold poppins-medium">{productData?.email || "N/A"}</h3>
                    </motion.div>
                  </>
                )}
              </motion.div>
            ))}
          </section>

          {/* Modal */}
          <AnimatePresence>
            {isModalOpen && (
              <motion.div
                id="modal-background"
                className="fixed inset-0 bg-[#00000082] bg-opacity-50 flex justify-center items-center z-50"
                onClick={handleBackgroundClick}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                <motion.div
                  className="bg-[#8B2433] px-7 pt-4 pb-8 rounded-xl shadow-lg text-center relative"
                  onClick={(e) => e.stopPropagation()}
                  initial={{ scale: 0.9, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0.9, opacity: 0 }}
                >
                  <motion.button
                    className="absolute top-3 right-2 text-[#8B2433] hover:text-gray-200 transition-colors"
                    onClick={closeModal}
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                  >
                    <div className="popup-close">
                      <IoIosCloseCircleOutline className="text-white h-8 w-8 cursor-pointer" />
                    </div>
                  </motion.button>
                  <h2 className="font-bold poppins-medium text-3xl text-[#fff] mb-5 ">QR Code</h2>
                  <div
                    style={{
                      padding: "0px",
                      border: "4px solid #8B2433",
                      borderRadius: "0px",
                      display: "inline-block",
                      background: "#fff",
                    }}
                  >
                  <QRCode
                    value={
                      frontendApiUrl && productData?.slug
                      ? `${frontendApiUrl}restaurant/${productData.slug}`
                      : "No content available"
                    }
                    bgColor="#8B2433"
                    fgColor="#fff"
                    size={230}
                    />
                    </div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>
          <ChartDashboard />
          <section>
            <motion.h3
              className="text-2xl font-bold mt-10 mb-2 poppins-medium"
              initial={{ x: -20, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ delay: 0.2 }}
            >
              Vini Recenti
            </motion.h3>

            <motion.div
              className="dash-table-main w-auto h-auto bg-[#F8F8F8] border-[1.5px] border-[#8B24332B] text-[#303030] pt-3 pb-0 px-6  shadow overflow-hidden"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              <table className="w-full text-left ristoranti_table">
                <thead>
                  <tr className="border-b">
                    <th className="pb-3 pr-2 text-left poppins-medium">Immagine</th>
                    <th className="pb-3 px-2 text-left poppins-medium">Nome</th>
                    <th className="pb-3 px-2 text-left poppins-medium">Tipologia</th>
                    <th className="pb-3 px-2 text-left poppins-medium">Nazione</th>
                    <th className="pb-3 px-2 text-left poppins-medium flex flex-col"><span>Prezzo Pagato</span> <span>(IVA eslcusa)</span></th>
                    <th className="pb-3 px-2 text-left poppins-medium">Quantità</th>
                    <th className="pb-3 px-2 text-left poppins-medium">Prezzo totale</th>
                  </tr>
                </thead>
                {isLoading ? (
                  <TableLoader />
                ) : (
                  <tbody>
                    {restaurantData && restaurantData.length > 0 ? (
                      restaurantData.map((restaurant, index) => (
                        <motion.tr
                          key={index}
                          className="border-b"
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: index * 0.05 }}
                        >
                          <td className="py-4 text-left poppins-regular">
                            <motion.img
                              src={restaurant?.wine?.image && restaurant?.wine?.image !== "" ? restaurant?.wine?.image : defaultImage}
                              alt={restaurant?.wine?.name}
                              className="w-12 bg-white h-12 border border-[#68172394] rounded-lg"
                              whileHover={{ scale: 1.05 }}
                            />
                          </td>
                          <td className="py-4 px-2 text-left poppins-regular">{restaurant?.wine?.name || "N/A"}</td>
                          <td className="py-4 px-2 text-left poppins-regular">{restaurant?.wine?.product_type || "N/A"}</td>
                          <td className="py-4 px-2 text-left poppins-regular">{restaurant?.country || "N/A"}</td>
                          <td className="py-4 px-2 text-left poppins-regular">{restaurant?.price || "N/A"}</td>
                          <td className="py-4 px-2 text-left poppins-regular">
                            <motion.span
                              className="inline-flex items-center justify-center bg-[#8B2433A6] text-white rounded-full min-w-[32px] h-[32px]"
                              whileHover={{ scale: 1.1 }}
                            >
                              {restaurant?.bottles_quantity}
                            </motion.span>
                          </td>
                          <td className="py-4 px-2 text-left poppins-regular">
                            {restaurant?.price && restaurant?.bottles_quantity ? `${(restaurant.price * restaurant.bottles_quantity).toFixed(2)} €` : 'N/A'}
                          </td>
                        </motion.tr>
                      ))
                    ) : (
                      <motion.tr
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.4 }}
                      >
                        <td colSpan={7} className="text-center py-4">No Orders found</td>
                      </motion.tr>
                    )}
                  </tbody>
                )}
              </table>
            </motion.div>

            {restaurantData && restaurantData.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
              >
                <PaginationComponent
                  page={page}
                  totalPages={totalPages}
                  rowsPerPage={rowsPerPage}
                  setPage={setPage}
                  setRowsPerPage={setRowsPerPage}
                />
              </motion.div>
            )}
          </section>
        </motion.div>
      </AnimatePresence>
    </div>
  );
}
